import React, { useState, useEffect, ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import FormGroup from "../../../../components/common/FormGroup";
import TextInput from "../../../../components/ui/TextInput";
import Button from "../../../../components/ui/Button";
import { FileInput } from "../../../../components/common/FileInput";
import ImageSpecsBadges from "../../../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../../../hooks/useImageValidation";
import Dropdown from "../../../../components/common/Dropdown";
import { createReward, getProgressionConfig, getLevels, LevelResponse } from "../../../../api/progression";
import { uploadFileToS3 } from "../../../../api/s3";
import { IMAGE_MAX_SIZE_200_KB } from "../../../../utils/image";
import i18n from "../../../../i18n";
import styles from "./CreateReward.module.css";
import stepStyles from "../createStep.module.css";
import pageHeader from "../../../../components/layouts/PageHeader.module.css";

interface CreateRewardProps {
    navigateToNext: () => void;
}

const CreateReward: React.FC<CreateRewardProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const token = localStorage.getItem("token") || "";

    const linkId = trackData?.trackId || trackData?.id;

    const [name, setName] = useState<string>("");
    const [popupText, setPopupText] = useState<string>("");
    const [descriptionText, setDescriptionText] = useState<string>("");
    const [imageUrl, setImageUrl] = useState<string>("");
    const [selectedLevelId, setSelectedLevelId] = useState<string>("");
    const [progressionConfigId, setProgressionConfigId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [uploadingImage, setUploadingImage] = useState<boolean>(false);
    const [levels, setLevels] = useState<LevelResponse[]>([]);
    const imageValidation = useImageValidation({
        type: "square",
        imageUrl: imageUrl || undefined,
    });

    useEffect(() => {
        const loadData = async () => {
            if (!trackData.trackId) return;

            try {
                const configResponse = await getProgressionConfig({ token, trackId: trackData.trackId });
                if (configResponse?.progressionConfig) {
                    setProgressionConfigId(configResponse.progressionConfig.id);
                    const levelsResponse = await getLevels({
                        token,
                        progressionConfigId: configResponse.progressionConfig.id,
                    });
                    if (levelsResponse?.levels) {
                        setLevels(levelsResponse.levels.sort((a, b) => a.position - b.position));
                    }
                }
            } catch (error) {
                console.error("Error loading data:", error);
            }
        };

        loadData();
    }, [trackData.trackId, token]);

    const handleImageUpload = async (files: File[] | File) => {
        const file = Array.isArray(files) ? files[0] : files;
        if (!file) return;

        setUploadingImage(true);
        await imageValidation.validateImage(file);
        try {
            const response = await uploadFileToS3({ token, file });
            if (response?.url) {
                setImageUrl(response.url);
            }
        } catch (error) {
            console.error("Error uploading image:", error);
        } finally {
            setUploadingImage(false);
        }
    };

    const handleSave = async () => {
        if (!progressionConfigId || !name || !selectedLevelId || !imageUrl || !popupText || !descriptionText) return;

        setLoading(true);
        try {
            await createReward({
                token,
                progressionConfigId,
                name,
                popupText: popupText || undefined,
                descriptionText: descriptionText || undefined,
                imageUrl: imageUrl || undefined,
                levelId: selectedLevelId || undefined,
                language: trackData.language || "EN",
            });
            navigateToNext();
        } catch (error) {
            console.error("Error creating reward:", error);
        } finally {
            setLoading(false);
        }
    };

    const getLocalizedLevelName = (level: LevelResponse) => {
        const langCode = i18n.language?.toUpperCase();
        const localized = level.levelLocalized?.find((loc) => loc.language?.toUpperCase() === langCode);
        return localized?.name || level.levelLocalized?.[0]?.name || `${t("level")} ${level.position}`;
    };

    const levelOptions = levels.map((level) => ({
        value: level.id,
        label: `${level.position}. ${getLocalizedLevelName(level)}`,
    }));

    return (
        <div className={styles.container}>
            {/* Breadcrumb */}
            <div className={styles.breadcrumb}>
                <Link to={`/create/tour/${linkId}/badges`}>{t("progression")}</Link>
                <span>›</span>
                <Link to={`/create/tour/${linkId}/rewards`}>{t("rewards")}</Link>
                <span>›</span>
                <span style={{ color: "var(--green-600)" }}>{t("createReward")}</span>
            </div>

            {/* Header */}
            <div className={pageHeader.withBreadcrumb}>
                <div className={pageHeader.titleSection}>
                    <div className={pageHeader.content}>
                        <h2 className="settings-title">{t("createReward")}</h2>
                        <p className={pageHeader.subtitle}>{t("createRewardDescription")}</p>
                    </div>
                </div>
            </div>

            {/* Form */}
            <div className={styles.sectionCard}>
                {/* Reward name */}
                <FormGroup
                    title={t("rewardName")}
                    subtitle={t("rewardNameDescription")}
                    tooltip={t("doNotTranslateTooltip")}
                    required
                >
                    <TextInput
                        placeholder={t("enterRewardName")}
                        value={name}
                        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setName(e.target.value)}
                        size="medium"
                    />
                </FormGroup>

                {/* Conditions */}
                <div className={styles.conditionsCard}>
                    <h3 className={styles.conditionsTitle}>{t("obtentionConditions")}</h3>
                    <p className={styles.conditionsDescription}>{t("obtentionConditionsDescription")}</p>

                    {/* Level to reach */}
                    <FormGroup title={t("levelToReach")} subtitle={t("levelToReachDescription")} required>
                        <Dropdown
                            label=""
                            options={levelOptions}
                            value={selectedLevelId}
                            onChange={setSelectedLevelId}
                            placeholder={t("selectLevel")}
                            loading={false}
                            error={false}
                        />
                    </FormGroup>
                </div>

                {/* Image */}
                <FormGroup title={t("image")} subtitle={t("rewardImageDescription")} required>
                    <ImageSpecsBadges spec="square" warnings={imageValidation.warnings} />
                    {imageUrl && (
                        <div style={{ marginBottom: "12px" }}>
                            <img
                                src={imageUrl}
                                alt={t("rewardImage")}
                                style={{
                                    maxWidth: "150px",
                                    maxHeight: "150px",
                                    objectFit: "contain",
                                    borderRadius: "8px",
                                }}
                            />
                        </div>
                    )}
                    <FileInput
                        onFileSelected={handleImageUpload}
                        placeholder={uploadingImage ? t("uploading") : t("dragDropOrClick")}
                        disabled={uploadingImage}
                        acceptedFileTypes={{ "image/*": [".png", ".jpg", ".jpeg", ".webp"] }}
                        maxSize={IMAGE_MAX_SIZE_200_KB}
                        maxSizeLabel={t("imageMaxSize200kb")}
                    />
                </FormGroup>

                {/* Popup text */}
                <FormGroup
                    title={t("rewardPopupText")}
                    subtitle={t("rewardPopupTextDescription")}
                    tooltip={t("doNotTranslateTooltip")}
                    required
                >
                    <TextInput
                        placeholder={t("enterText")}
                        value={popupText}
                        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                            setPopupText(e.target.value)
                        }
                        size="big"
                        numberOfLines={4}
                        style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            padding: "12px",
                            width: "100%",
                            boxSizing: "border-box",
                        }}
                    />
                </FormGroup>

                {/* Card text */}
                <FormGroup
                    title={t("rewardCardText")}
                    subtitle={t("rewardCardTextDescription")}
                    tooltip={t("doNotTranslateTooltip")}
                    required
                >
                    <TextInput
                        placeholder={t("enterText")}
                        value={descriptionText}
                        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                            setDescriptionText(e.target.value)
                        }
                        size="big"
                        numberOfLines={4}
                        style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            padding: "12px",
                            width: "100%",
                            boxSizing: "border-box",
                        }}
                    />
                </FormGroup>
            </div>

            {/* Footer buttons — mock .foot */}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={loading ? t("saving") : t("create")}
                    onClick={handleSave}
                    disabled={
                        !name ||
                        !selectedLevelId ||
                        !imageUrl ||
                        !popupText ||
                        !descriptionText ||
                        loading ||
                        uploadingImage
                    }
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>
        </div>
    );
};

export default CreateReward;
